import type { Agenda, Meeting } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { DatePicker } from "@repo/ui/components/Base/DatePicker";
import { WeekdayPicker } from "@repo/ui/components/Base/WeekdayPicker";
import { MonthPicker } from "@repo/ui/components/Base/MonthPicker";
import { Input } from "@repo/ui/components/Base/Input";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { Formik } from "formik";
import { useState } from "react";
import * as yup from "yup";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/Base/Dialog";
import { wholeNumber } from "@repo/shared/utils";
import {
  type RecurrenceSettings,
  computeReoccurrences,
  intervalNames,
  intervalOptions,
  tweakSelections,
  weekdayIndexNames,
  weekdayIndexOptions,
  weekdays,
} from "@repo/shared/recurring";
import { TextArea } from "@repo/ui/components/Base/TextArea";
import { CheckboxGroupInput } from "@repo/ui/components/Base/CheckboxGroupInput";
import { TimeField } from "@repo/ui/components/Base/TimeField";
import { Time } from "@internationalized/date";
import { TagSelection } from "../tags";
import { addMinutes, differenceInMinutes, format } from "date-fns";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import { SimpleDate } from "@repo/lib/simpleDate";
import { ReoccurrencePreview } from "../common/ReoccurrencePreview";
import { useTrackedNewObjects } from "@/hooks/trackCreatedObjects";
import { useUserStore } from "@/stores/userStore";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { agendaTemplateService } from "@/services/agendaTemplateService";
import { dateSetTime } from "@repo/lib/dateSet";
import { useGetMeetingTypes } from "@/hooks/queryHooks/configHooks";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";
import { useLetterHeadList } from "@/hooks/queryHooks/letterHeadHooks";
import { agendaModuleRepo } from "@/repositories/agendaModuleRepo";
import { agendaRepo } from "@/repositories/agendaRepo";
import { meetingRepo } from "@/repositories/meetingRepo";

const maxRecurrences = 100;

export interface ReoccurringTemplateMeetingsFormProps {
  tenantId: string;
  template?: Agenda;
  handleCancel: () => void;
  handleComplete: () => void;
  withinDialog?: boolean;
  templates?: Agenda[];
}

type MeetingCore = Pick<
  Meeting,
  | "startTime"
  | "endTime"
  | "name"
  | "description"
  | "letterHeadId"
  | "meetingType"
  | "meetingTypeOption"
  | "tags"
>;

type RecurrenceTemplateSettings = RecurrenceSettings &
  MeetingCore & {
    templateId?: string;
  };

const generateMeeting = (values: RecurrenceTemplateSettings) => {
  return {
    startTime: values.startTime,
    endTime: values.endTime,
    name: values.name,
    description: values.description,
    letterHeadId: values.letterHeadId,
    meetingType: values.meetingType,
    meetingTypeOption: values.meetingTypeOption,
    tags: values.tags,
  } satisfies MeetingCore;
};

export const ReoccurringTemplateMeetingsForm = ({
  tenantId,
  template,
  handleCancel,
  handleComplete,
  withinDialog,
  templates,
}: ReoccurringTemplateMeetingsFormProps) => {
  useBlockUnload();

  const activeTeam = useUserStore((s) => s.activeTeam);

  const { data: letterheads } = useLetterHeadList(tenantId);
  const { data: meetingTypes } = useGetMeetingTypes();

  const validationFields: Record<string, yup.AnySchema> = {
    name: yup.string().required("Meeting name is required"),
  };

  if (templates?.length) {
    validationFields.templateId = yup.string().required("Template is required");
  }

  const validationSchema = yup.object(validationFields);

  const trackNew = useTrackedNewObjects();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submission, setSubmission] =
    useState<React.FormEvent<HTMLFormElement>>();

  const freshMeeting = meetingRepo.init();

  const calcMeetingDuration = (startTime: Date, endTime: Date) => {
    return differenceInMinutes(endTime, startTime);
  };

  const initialDate = freshMeeting.startTime ?? new Date();
  const weekdaySelection: boolean[] = [];
  const monthSelection: boolean[] = [];

  const initialSimpleDate = new SimpleDate(initialDate);
  weekdaySelection[initialSimpleDate.weekday] = true;
  monthSelection[initialSimpleDate.month - 1] = true;

  if (!activeTeam || activeTeam === "org") {
    return (
      <p>Cannot create reoccurring meetings without first selecting a team.</p>
    );
  }

  const defaultStartTime = activeTeam.defaultStartTime ?? {
    hours: 10,
    minutes: 0,
  };
  const defaultEndTime = activeTeam.defaultEndTime ?? { hours: 11, minutes: 0 };

  const initialValues: RecurrenceTemplateSettings = {
    // meeting fields
    startTime: dateSetTime(
      initialDate,
      defaultStartTime.hours,
      defaultStartTime.minutes
    ),
    endTime: dateSetTime(
      initialDate,
      defaultEndTime.hours,
      defaultEndTime.minutes
    ),
    name: template?.name || "",
    description: template?.description || "",
    letterHeadId: template?.letterHeadId,
    meetingType: freshMeeting.meetingType,
    meetingTypeOption: freshMeeting.meetingTypeOption,
    tags: [],

    // recurrence schedule
    interval: "yearly",
    startDate: new Date(),
    skip: 1,
    weekdaySelection,
    monthSelection,
    nthWeekday: false,
    weekdayIndex: 1,
    weekdayDay: 1,
    mode: "count",
    count: 1,
    until: new Date(),

    // template
    templateId: template?.id || "",
  };

  return (
    <div className="w-full">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          const occurrences = computeReoccurrences({
            values,
            maxRecurrences,
          });
          const meeting = generateMeeting(values);

          console.log(occurrences, meeting);

          const start = meeting.startTime;
          const end = meeting.endTime;
          const offset = start.getTime() - new SimpleDate(start).date.getTime(); // time offset into day
          const interval = end.getTime() - start.getTime(); // meeting length

          const templateToUse =
            templates?.find((t) => t.id === values.templateId) ?? template;

          if (!templateToUse) {
            console.error("No template provided for reoccurring meetings.");
            return;
          }

          const agenda: Agenda = {
            ...templateToUse,
            // remove template properties
            template: undefined,
            templateId: undefined,
          };
          const agendaModules = agenda?.agendaModules ?? [];

          for (const date of occurrences) {
            // copy agenda modules
            const agendaModuleIds: string[] = [];

            for (const agendaModule of agendaModules) {
              // copy agenda items
              const agendaItemIds: string[] = [];
              for (const agendaItem of agendaModule.agendaItems ?? []) {
                const copiedAgendaItem =
                  agendaTemplateService.getCopiedAgendaItem(
                    {
                      ...agendaItem,
                      template: undefined,
                      templateId: undefined,
                    },
                    activeTeam.id
                  );

                const newAgendaItem = await agendaItemRepo.create(
                  tenantId,
                  copiedAgendaItem
                );
                if (newAgendaItem) agendaItemIds.push(newAgendaItem.id);
                trackNew("agendaItem", newAgendaItem);
              }

              const copiedAgendaModule =
                agendaTemplateService.getCopiedAgendaModule(
                  {
                    ...agendaModule,
                    template: undefined,
                    templateId: undefined,
                  },
                  activeTeam.id,
                  agendaItemIds
                );

              const newAgendaModule = await agendaModuleRepo.create(
                tenantId,
                copiedAgendaModule
              );
              if (newAgendaModule) agendaModuleIds.push(newAgendaModule.id);
              trackNew("agendaModule", newAgendaModule);
            }

            // copy agenda
            let agendaId = "";

            // if(true) for sanity / consistency
            if (agenda) {
              const copiedAgenda = agendaTemplateService.getCopiedAgenda(
                { ...agenda, template: undefined, templateId: undefined },
                activeTeam.id,
                agendaModuleIds
              );

              const newAgenda = await agendaRepo.create(tenantId, copiedAgenda);
              if (newAgenda) agendaId = newAgenda.id;
              trackNew("agenda", newAgenda);
            }

            // copy meeting
            const copiedMeeting: Partial<Meeting> = {
              ...freshMeeting,
              ...meeting,
              id: undefined, // don't overwrite old item
              teamId: activeTeam.id,
              agendaId,
              agenda: undefined,
              startTime: new Date(date.getTime() + offset),
              endTime: new Date(date.getTime() + offset + interval),

              createdAt: undefined,
              createdBy: undefined,
              updatedAt: undefined,
              updatedBy: undefined,
            };

            const newMeeting = await meetingRepo.create(
              tenantId,
              copiedMeeting
            );
            trackNew("meeting", newMeeting);
          }

          console.log("success");

          handleComplete();
        }}
      >
        {({
          values,
          handleSubmit,
          handleBlur,
          handleChange,
          setFieldValue,
          errors,
          touched,
          isValid,
          isSubmitting,
        }) => {
          const occurrences = computeReoccurrences({
            values,
            maxRecurrences,
          });
          const meeting = generateMeeting(values);

          const selectedTemplate =
            templates?.find((t) => t.id === values.templateId) ?? template;

          const availableMeetingOptions = () => {
            const meetingType = meetingTypes?.meetingTypes.filter(
              (meetingType) => values.meetingType === meetingType.value
            );
            if (meetingType && meetingType?.length > 0) {
              return meetingType[0].options;
            }
            return;
          };

          const meetingOptions = availableMeetingOptions();

          const startTime = values.startTime;
          const endTime = values.endTime;

          const footer = (
            <>
              <Button
                type="button"
                disabled={isSubmitting}
                variant="destructive"
                onClick={() => handleCancel()}
              >
                Cancel
              </Button>
              <div className="flex-1" />
              <Button
                type="submit"
                disabled={!isValid || isSubmitting || !occurrences.length}
              >
                Create {occurrences.length} copies
              </Button>
            </>
          );

          const form = (
            <div className="flex flex-col gap-y-4">
              {templates && templates.length > 0 && (
                <div>
                  <SelectInput
                    label="*Template"
                    value={values.templateId ?? ""}
                    onValueChange={(value) => {
                      setFieldValue("templateId", value);

                      const tmpl = templates.find((t) => t.id === value);
                      if (tmpl) {
                        setFieldValue("name", tmpl.name ?? "");
                        setFieldValue("description", tmpl.description ?? "");
                        setFieldValue("letterHeadId", tmpl.letterHeadId);
                      }
                    }}
                    options={templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                    error={!!errors.templateId && touched.templateId}
                    errorMessage={errors.templateId as string}
                  />
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold mt-2">Meeting</h2>
                <p className="mt-1">
                  Set up fields for a meeting for this template:
                </p>
              </div>
              {/* Meeting form */}
              <SelectInput
                label="Meeting Type"
                value={values.meetingType}
                onValueChange={(value) => {
                  // convert meetingType value to label
                  const typeLabel = (meetingType: string) =>
                    meetingTypes?.meetingTypes?.find(
                      (type) => type.value === meetingType
                    )?.label;

                  // if meeting title is not set or equals previous meetingType label, change it to new meetingType label
                  if (
                    !values.name ||
                    values.name === typeLabel(values.meetingType ?? "")
                  ) {
                    setFieldValue("name", typeLabel(value));
                  }

                  setFieldValue("meetingType", value);
                  setFieldValue("meetingTypeOption", "");
                }}
                options={
                  meetingTypes?.meetingTypes
                    ? meetingTypes?.meetingTypes.map((meetingType) => (
                        <SelectItem
                          key={meetingType.value}
                          value={meetingType.value}
                        >
                          {meetingType.label}
                        </SelectItem>
                      ))
                    : []
                }
              />
              {meetingOptions &&
                (() => {
                  switch (meetingOptions.type) {
                    case "checkbox":
                      return (
                        <CheckboxGroupInput
                          wrap
                          label={meetingOptions.optionsLabel}
                          options={meetingOptions.options.map((option) => ({
                            key: option.value,
                            label: option.label,
                          }))}
                          value={
                            values.meetingTypeOption
                              ? typeof values.meetingTypeOption === "string"
                                ? [values.meetingTypeOption]
                                : values.meetingTypeOption
                              : []
                          }
                          onValueChange={(value) => {
                            setFieldValue("meetingTypeOption", value);
                          }}
                        />
                      );
                      break;

                    default:
                      return (
                        <SelectInput
                          label={meetingOptions?.optionsLabel}
                          value={
                            values.meetingTypeOption
                              ? typeof values.meetingTypeOption === "string"
                                ? values.meetingTypeOption
                                : values.meetingTypeOption[0]
                              : undefined
                          }
                          onValueChange={(value) => {
                            setFieldValue("meetingTypeOption", value);
                          }}
                          options={
                            meetingOptions?.options
                              ? meetingOptions.options.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))
                              : []
                          }
                        />
                      );
                  }
                })()}
              <Input
                label="*Meeting Title"
                id="name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.name && touched.name}
                errorMessage={errors.name}
              />
              <TextArea
                label="Additional Text"
                id="description"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.description && touched.description}
                errorMessage={errors.description}
              />
              {!selectedTemplate?.letterHeadId && (
                <SelectInput
                  label="Letterhead"
                  value={values.letterHeadId ?? ""}
                  onValueChange={(value) => {
                    setFieldValue("letterHeadId", value);
                    setFieldValue(
                      "letterHead",
                      letterheads?.find((lh) => lh.id === value)
                    );
                  }}
                  options={
                    letterheads
                      ? letterheads.map((lh) => (
                          <SelectItem key={lh.id} value={lh.id}>
                            {lh.viewName ?? lh.name}
                          </SelectItem>
                        ))
                      : []
                  }
                />
              )}
              <div className="flex gap-y-1 mt-1.5 flex-col">
                <DatePicker
                  id="startTime"
                  label="Date / Time"
                  date={startTime}
                  onDateChange={(date) => {
                    if (!date) return;

                    const start = values.startTime ?? new Date();

                    const [weekdays, months] = tweakSelections(
                      values.startDate,
                      date,
                      values.weekdaySelection,
                      values.monthSelection
                    );

                    date = new Date(
                      date.getFullYear(),
                      date.getMonth(),
                      date.getDate(),
                      start.getHours(),
                      start.getMinutes()
                    );
                    setFieldValue("startTime", date);

                    setFieldValue("startDate", date);
                    setFieldValue("weekdaySelection", weekdays);
                    setFieldValue("monthSelection", months);
                  }}
                />
                <div className="flex flex-row gap-2 items-center">
                  <span>From</span>
                  <TimeField
                    disableHasTimeChange
                    value={
                      new Time(startTime?.getHours(), startTime?.getMinutes())
                    }
                    onChange={(time) => {
                      const start = startTime ?? new Date();

                      const newStart = new Date(
                        start.getFullYear(),
                        start.getMonth(),
                        start.getDate(),
                        time.hour,
                        time.minute
                      );
                      setFieldValue("startTime", newStart);
                      setFieldValue(
                        "endTime",
                        addMinutes(
                          newStart,
                          calcMeetingDuration(startTime, endTime)
                        )
                      );
                    }}
                  />
                  <span>To</span>
                  <TimeField
                    disableHasTimeChange
                    // hasTime={endTimeSet}
                    value={new Time(endTime?.getHours(), endTime?.getMinutes())}
                    onChange={(time) => {
                      const end = endTime ?? new Date();

                      const newEnd = new Date(
                        end.getFullYear(),
                        end.getMonth(),
                        end.getDate(),
                        time.hour,
                        time.minute
                      );
                      setFieldValue("endTime", newEnd);
                    }}
                    // onHasTimeChange={setEndTimeSet}
                  />
                </div>
              </div>

              <TagSelection
                label="Tags"
                value={values.tags ?? []}
                onChange={(newTags) => setFieldValue("tags", newTags)}
                enableAutocomplete={true}
                useStandardizedTags={true}
              />

              <div className="h-2" />
              <hr />

              {/* Recurrence form */}
              <div>
                <h2 className="text-2xl font-bold mt-2">Schedule</h2>
                <p className="mt-1">
                  Configure schedule for generating meetings using this meeting,
                  '<strong>{meeting.name}</strong>':
                </p>
              </div>
              <div className="flex flex-col gap-y-2">
                <DatePicker
                  id="startDate"
                  label="Starts (Configure above)"
                  date={values.startDate}
                  onDateChange={() => {}}
                  disabled
                />
              </div>
              <div>
                <p className="text-slate-800 text-base leading-5">
                  Recurs Every
                </p>
                <div className="flex flex-row gap-x-1">
                  <Input
                    id="skip"
                    name="skip"
                    containerStyle="w-0 grow"
                    onBlur={(evt) => {
                      setFieldValue("skip", wholeNumber(evt.target.value));
                      handleBlur(evt);
                    }}
                    value={values.skip}
                    onChange={handleChange}
                  />
                  <SelectInput
                    value={values.interval}
                    onValueChange={(value) => {
                      setFieldValue("interval", value);
                    }}
                    containerStyle="w-0 grow-[3]"
                    options={intervalOptions.map((interval) => (
                      <SelectItem key={interval} value={interval}>
                        {intervalNames[interval]}
                      </SelectItem>
                    ))}
                  />
                </div>
              </div>
              {values.interval === "weekly" ? (
                <div>
                  <WeekdayPicker
                    id="weekdaySelection"
                    label="Repeat On &nbsp; (Note: multiple selected weekdays below will create multiple copies within each week period.)"
                    value={values.weekdaySelection}
                    onChange={(value) => {
                      setFieldValue("weekdaySelection", value);
                    }}
                  />
                </div>
              ) : values.interval === "yearly" ? (
                <div>
                  <MonthPicker
                    id="monthSelection"
                    label="Repeat On &nbsp; (Note: multiple selected months below will create multiple copies within each year period.)"
                    value={values.monthSelection}
                    onChange={(value) => {
                      setFieldValue("monthSelection", value);
                    }}
                  />
                </div>
              ) : null}
              {(values.interval === "monthly" ||
                values.interval === "yearly") && (
                <div>
                  <CheckboxInput
                    orientation="horizontal"
                    label="nth Weekday"
                    checked={values.nthWeekday}
                    onCheckedChange={(value) =>
                      setFieldValue("nthWeekday", value)
                    }
                  />
                  {values.nthWeekday && (
                    <>
                      <p className="text-slate-800 text-base leading-5 mt-4">
                        On the...
                      </p>
                      <div className="flex flex-row gap-x-1 items-center">
                        <SelectInput
                          containerStyle="w-0 grow"
                          value={"" + values.weekdayIndex}
                          onValueChange={(value) => {
                            setFieldValue("weekdayIndex", +value);
                          }}
                          options={weekdayIndexOptions.map((ind) => (
                            <SelectItem key={ind} value={ind}>
                              {weekdayIndexNames[ind]}
                            </SelectItem>
                          ))}
                        />
                        <SelectInput
                          containerStyle="w-0 grow-[3]"
                          value={"" + values.weekdayDay}
                          onValueChange={(value) => {
                            setFieldValue("weekdayDay", +value);
                          }}
                          options={weekdays.map((wd, i) => (
                            <SelectItem key={i} value={"" + i}>
                              {wd}
                            </SelectItem>
                          ))}
                        />
                        <p className="ml-1">Of the month</p>
                      </div>
                    </>
                  )}
                </div>
              )}
              <div>
                <p className="text-slate-800 text-base leading-5">Ends</p>
                <div className="flex flex-row gap-x-1">
                  <SelectInput
                    containerStyle="w-0 grow"
                    value={values.mode}
                    onValueChange={(value) => {
                      setFieldValue("mode", value);
                    }}
                    options={[
                      <SelectItem key="until" value="until">
                        On
                      </SelectItem>,
                      <SelectItem key="count" value="count">
                        After
                      </SelectItem>,
                    ]}
                  />
                  <div className="w-0 grow-[2] flex flex-row gap-x-2 items-center">
                    {values.mode === "until" ? (
                      <DatePicker
                        id="until"
                        className="grow mt-1.5"
                        date={values.until}
                        onDateChange={(value) => {
                          setFieldValue("until", value);
                        }}
                        calendarProps={{
                          fromDate: values.startDate,
                        }}
                      />
                    ) : (
                      <>
                        <Input
                          id="count"
                          name="count"
                          containerStyle="grow"
                          onBlur={(evt) => {
                            setFieldValue(
                              "count",
                              wholeNumber(evt.target.value, maxRecurrences)
                            );
                            handleBlur(evt);
                          }}
                          value={values.count}
                          onChange={handleChange}
                        />
                        <p className="mt-1.5">Occurrences</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-slate-800 text-base leading-5">
                Recurrence Schedule Preview:
              </p>
              <ReoccurrencePreview occurrences={occurrences} />

              {!withinDialog && (
                <div className="flex flex-row justify-between">{footer}</div>
              )}
            </div>
          );

          return (
            <div>
              <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      Confirm Creation of {occurrences.length} Meetings(s)
                    </DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <p className="my-2">
                      Please confirm creation of {occurrences.length}{" "}
                      meetings(s).
                    </p>
                    <p>
                      <strong>Notice: This action cannot be undone.</strong> If
                      you need to revert this change, you will need to manually
                      delete all {occurrences.length} meeting(s). The generation
                      process may also take a long time, especially if there are
                      many sections on the agenda. Do not refresh or leave the
                      page while it loads.
                    </p>
                    <p className="mt-5 font-bold">Meeting being generated:</p>
                    <div className="mt-4 mb-6 mx-3">
                      <p className="font-medium">{meeting.name}</p>
                      <p>
                        {format(startTime, "h:mm a")} -{" "}
                        {format(endTime, "h:mm a")}
                      </p>
                      <p className="text-xs">{meeting.description}</p>
                      <ul className="pl-2">
                        {selectedTemplate?.agendaModules?.map(
                          (agendaModule) => (
                            <li key={agendaModule.id}>
                              {agendaModule.name}
                              <ul className="pl-2">
                                {agendaModule.agendaItems?.map((agendaItem) => (
                                  <li key={agendaItem.id}>{agendaItem.name}</li>
                                ))}
                              </ul>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                    <p className="mt-5 font-bold">Schedule:</p>
                    <div className="mt-4 mb-6 mx-3">
                      <p>
                        Every {values.skip}{" "}
                        {intervalNames[values.interval].toLowerCase()}
                      </p>
                      <p>
                        Copies start from {occurrences[0]?.toDateString()} and
                        run to{" "}
                        {occurrences[occurrences.length - 1]?.toDateString()}
                      </p>
                      <p>{occurrences.length} total occurrences.</p>
                    </div>
                  </DialogBody>
                  <DialogFooter className="justify-between">
                    <Button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        setSubmission(undefined);
                        setConfirmOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <div className="flex-1" />
                    <Button
                      type="button"
                      disabled={isSubmitting}
                      variant="destructive"
                      onClick={() => {
                        handleSubmit(submission);
                      }}
                    >
                      Confirm
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <form
                onSubmit={(evt) => {
                  setSubmission(evt);
                  setConfirmOpen(true);
                  evt.preventDefault();
                }}
              >
                {withinDialog ? (
                  <>
                    <DialogBody>{form}</DialogBody>
                    <DialogFooter className="justify-between">
                      {footer}
                    </DialogFooter>
                  </>
                ) : (
                  form
                )}
              </form>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};
